// src/ChallengesPanel.js

import React, { useState, useEffect, useMemo } from "react";

const safeLoadJson = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const safeSaveJson = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

// Generate a short hero buddy code like HP-4F9X
const generateBuddyCode = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "HP-";
  for (let i = 0; i < 4; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
};

const ChallengesPanel = ({
  hero,
  progress,
  daysPerWeekByHero,
  blockLengthByHero,
  todayStr,
}) => {
  const heroId = hero.id;

  /* ============ BASIC STATS / WEEKLY CHALLENGE ============ */

  const heroProgress = progress[heroId] || {};
  const targetDays = (daysPerWeekByHero && daysPerWeekByHero[heroId]) || 4;

  const currentWeekNumber = useMemo(() => {
    const weeks = Object.keys(heroProgress)
      .map((w) => parseInt(w, 10))
      .filter((n) => !Number.isNaN(n));
    if (!weeks.length) return 1;
    return Math.max(...weeks);
  }, [heroProgress]);

  const currentWeekProg = heroProgress[currentWeekNumber] || {};
  const completedDaysThisWeek =
    Object.values(currentWeekProg).filter(Boolean).length;

  const totalSessions = useMemo(
    () =>
      Object.values(heroProgress).reduce((sum, weekProg) => {
        return sum + Object.values(weekProg).filter((done) => !!done).length;
      }, 0),
    [heroProgress]
  );

  const blockLength = (blockLengthByHero && blockLengthByHero[heroId]) || 9;

  /* ============ HERO XP & STREAKS ============ */

  // Simple XP system: 10 XP per completed session
  const XP_PER_SESSION = 10;
  const totalXp = totalSessions * XP_PER_SESSION;

  // Level up every 200 XP
  const level = Math.max(1, Math.floor(totalXp / 200) + 1);
  const xpIntoLevel = totalXp % 200;
  const xpToNext = 200 - xpIntoLevel;

  // Weekly streak: consecutive weeks (backwards from current) with at least one completed workout
  const currentWeeklyStreak = useMemo(() => {
    if (!Object.keys(heroProgress).length) return 0;

    let streak = 0;
    for (let w = currentWeekNumber; w >= 1; w--) {
      const weekProg = heroProgress[w];
      if (!weekProg) break;
      const didSomething = Object.values(weekProg).some(Boolean);
      if (didSomething) {
        streak += 1;
      } else {
        break;
      }
    }
    return streak;
  }, [heroProgress, currentWeekNumber]);

  /* ============ BUDDY SYSTEM ============ */

  const [buddyCode, setBuddyCode] = useState(() => {
    const stored = safeLoadJson("heroBuddyCode_v1", null);
    if (stored && typeof stored === "string") return stored;
    const fresh = generateBuddyCode();
    safeSaveJson("heroBuddyCode_v1", fresh);
    return fresh;
  });

  const [buddies, setBuddies] = useState(() =>
    safeLoadJson("heroBuddies_v1", [])
  );

  const [buddyInput, setBuddyInput] = useState("");

  useEffect(() => {
    safeSaveJson("heroBuddyCode_v1", buddyCode);
  }, [buddyCode]);

  useEffect(() => {
    safeSaveJson("heroBuddies_v1", buddies);
  }, [buddies]);

  const handleRegenerateCode = () => {
    const fresh = generateBuddyCode();
    setBuddyCode(fresh);
  };

  const handleAddBuddy = () => {
    const code = buddyInput.trim().toUpperCase();
    if (!code || code === buddyCode) {
      setBuddyInput("");
      return;
    }
    if (buddies.some((b) => b.code === code)) {
      setBuddyInput("");
      return;
    }
    const since = todayStr || new Date().toISOString().slice(0, 10);
    const label = `Hero Buddy ${code.slice(-2)}`;
    const newBuddy = { code, label, since };
    setBuddies((prev) => [newBuddy, ...prev]);
    setBuddyInput("");
  };

  const handleRemoveBuddy = (code) => {
    setBuddies((prev) => prev.filter((b) => b.code !== code));
  };

  /* ============ HERO FEED / MINI TIMELINE ============ */

  const [heroFeed, setHeroFeed] = useState(() =>
    safeLoadJson("heroFeed_v1", [])
  );
  const [newPost, setNewPost] = useState("");

  useEffect(() => {
    safeSaveJson("heroFeed_v1", heroFeed);
  }, [heroFeed]);

  useEffect(() => {
    // Only auto-initialize if the user hasn't started typing
    if (!newPost) {
      const base = `Week ${currentWeekNumber}: Completed ${completedDaysThisWeek}/${targetDays} days as ${hero.name}.`;
      setNewPost(base);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hero.name, currentWeekNumber, completedDaysThisWeek, targetDays]);

  const handlePostToFeed = () => {
    const text = newPost.trim();
    if (!text) return;
    const entry = {
      id: Date.now(),
      date: todayStr || new Date().toISOString().slice(0, 10),
      heroName: hero.name,
      universe: hero.universe,
      text,
      week: currentWeekNumber,
    };
    setHeroFeed((prev) => [entry, ...prev].slice(0, 50));
    setNewPost("");
  };

  /* ============ RESPONSIVE: MOBILE DETECTION ============ */

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 640; // Tailwind-ish "sm" breakpoint
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ============ SHARE STORY CARD TEXT ============ */

  const cardSummaryLine = `Week ${currentWeekNumber} • ${completedDaysThisWeek}/${targetDays} sessions`;
  const cardSubtitle = `${hero.universe} • ${blockLength}-week arc`;

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Challenges & Social</div>
        <div className="tag">Community • Buddies • Feed</div>
      </div>

      {/* SECTION 1 – Weekly Challenge Snapshot */}
      <div className="section-label">This Week’s Hero Challenge</div>
      <div
        className="profile-field-row"
        style={{
          display: "grid",
          gridTemplateColumns: "1.5fr 1fr",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background:
              "linear-gradient(135deg, rgba(56,189,248,0.12), rgba(15,23,42,0.95))",
            border: "1px solid rgba(56,189,248,0.4)",
          }}
        >
          <div
            style={{
              fontSize: "0.9rem",
              marginBottom: 4,
              opacity: 0.85,
            }}
          >
            Current hero:{" "}
            <strong>
              {hero.name} ({hero.universe})
            </strong>
          </div>
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Block:{" "}
            <strong>
              {blockLength}-week •{" "}
              {daysPerWeekByHero && daysPerWeekByHero[heroId]
                ? `${daysPerWeekByHero[heroId]} days/week`
                : "4–5 days/week"}
            </strong>
          </div>
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Week {currentWeekNumber}:{" "}
            <strong>
              {completedDaysThisWeek}/{targetDays} workouts completed
            </strong>
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              opacity: 0.8,
              marginTop: 4,
            }}
          >
            Mini-mission: hit all {targetDays} sessions this week and log them
            in your Workouts tab.
          </div>
        </div>

        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background: "rgba(15,23,42,0.96)",
            border: "1px solid rgba(148,163,184,0.6)",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Total sessions completed this arc:
          </div>
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              color: "#67e8f9",
              marginBottom: 2,
            }}
          >
            {totalSessions}
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              opacity: 0.8,
            }}
          >
            That’s your personal scoreboard. Each logged day is a brick in your
            hero origin story.
          </div>
        </div>
      </div>

      {/* SECTION 1B – Hero XP & Streaks */}
      <div className="section-label" style={{ marginTop: 16 }}>
        Hero XP & Streaks
      </div>
      <div
        className="profile-field-row"
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1.2fr",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {/* XP card */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background:
              "radial-gradient(circle at 10% 0%, rgba(56,189,248,0.3), rgba(15,23,42,0.98))",
            border: "1px solid rgba(56,189,248,0.7)",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Total Hero XP
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontSize: "1.8rem",
                fontWeight: 700,
              }}
            >
              {totalXp}
            </span>
            <span
              style={{
                fontSize: "0.8rem",
                opacity: 0.9,
              }}
            >
              XP • Level {level}
            </span>
          </div>

          <div
            style={{
              height: 8,
              borderRadius: 999,
              background: "rgba(15,23,42,0.8)",
              overflow: "hidden",
              marginBottom: 4,
              border: "1px solid rgba(148,163,184,0.6)",
            }}
          >
            <div
              style={{
                width: `${(xpIntoLevel / 200) * 100}%`,
                transition: "width 0.3s ease-out",
                height: "100%",
                borderRadius: 999,
                background:
                  "linear-gradient(90deg, rgba(56,189,248,1), rgba(52,211,153,0.9))",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.85,
            }}
          >
            {xpIntoLevel}/{200} XP this level • {xpToNext} XP to Level{" "}
            {level + 1}.
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
              marginTop: 4,
            }}
          >
            Each logged workout grants <strong>{XP_PER_SESSION} XP</strong>.
            Keep stacking sessions to level up your hero arc.
          </div>
        </div>

        {/* Streak card */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background: "rgba(15,23,42,0.96)",
            border: "1px solid rgba(148,163,184,0.6)",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Weekly streak
          </div>
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              color: "#fbbf24",
              marginBottom: 2,
            }}
          >
            {currentWeeklyStreak} week
            {currentWeeklyStreak === 1 ? "" : "s"}
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              opacity: 0.9,
              marginBottom: 4,
            }}
          >
            Weeks in a row with at least one logged workout as{" "}
            <strong>{hero.name}</strong>.
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
            }}
          >
            Protect the streak. Even one session in a busy week keeps your hero
            arc alive.
          </div>
        </div>
      </div>

      {/* SECTION 2 – Buddy System */}
      <div className="section-label" style={{ marginTop: 20 }}>
        Hero Buddy System
      </div>

      <div
        className="profile-field-row"
        style={{ display: "grid", gridTemplateColumns: "1.2fr 1.4fr", gap: 12 }}
      >
        {/* Your code */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background:
              "radial-gradient(circle at 10% 0%, rgba(56,189,248,0.3), rgba(15,23,42,0.98))",
            border: "1px solid rgba(56,189,248,0.6)",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>Your Hero Buddy code</span>
            <button
              onClick={handleRegenerateCode}
              style={{
                fontSize: "0.7rem",
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,0.7)",
                background: "rgba(15,23,42,0.85)",
                color: "#e5e7eb",
                cursor: "pointer",
              }}
            >
              New code
            </button>
          </div>
          <div
            style={{
              fontSize: "1.3rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              marginBottom: 4,
            }}
          >
            {buddyCode}
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
            }}
          >
            Share this with one trusted friend. When they add it on their app,
            you’ll both see each other in your Buddy list.
          </div>
        </div>

        {/* Add buddy + list */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background: "rgba(15,23,42,0.96)",
            border: "1px solid rgba(148,163,184,0.6)",
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 4,
            }}
          >
            Add a buddy’s code
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <input
              value={buddyInput}
              onChange={(e) => setBuddyInput(e.target.value.toUpperCase())}
              placeholder="Enter their code (e.g. HP-4F9X)"
              style={{
                flex: 1,
                padding: "6px 8px",
                fontSize: "0.8rem",
                borderRadius: 999,
              }}
            />
            <button
              onClick={handleAddBuddy}
              style={{
                padding: "6px 10px",
                fontSize: "0.8rem",
                borderRadius: 999,
                border: "1px solid rgba(56,189,248,0.7)",
                background:
                  "radial-gradient(circle at 30% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
                color: "#e0f2fe",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Add
            </button>
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              opacity: 0.9,
              marginBottom: 4,
            }}
          >
            Your buddies
          </div>
          {buddies.length === 0 ? (
            <div
              style={{
                fontSize: "0.8rem",
                opacity: 0.7,
              }}
            >
              No buddies yet. Share your code, or add a friend’s, then keep each
              other accountable.
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                maxHeight: 140,
                overflowY: "auto",
              }}
            >
              {buddies.map((b) => (
                <div
                  key={b.code}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "4px 6px",
                    borderRadius: 999,
                    background: "rgba(15,23,42,0.9)",
                    border: "1px solid rgba(148,163,184,0.5)",
                    fontSize: "0.8rem",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{b.label}</div>
                    <div style={{ opacity: 0.75, fontSize: "0.75rem" }}>
                      Code: {b.code} • since {b.since}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveBuddy(b.code)}
                    style={{
                      fontSize: "0.7rem",
                      padding: "3px 8px",
                      borderRadius: 999,
                      border: "1px solid rgba(248,113,113,0.6)",
                      background: "rgba(127,29,29,0.5)",
                      color: "#fee2e2",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3 – Hero Feed / Mini Timeline */}
      <div className="section-label" style={{ marginTop: 22 }}>
        Hero Feed – Mini Timeline
      </div>

      {/* Feed card + Story banner stacked vertically */}
      <div
        className="profile-field-row"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          alignItems: "stretch",
        }}
      >
        {/* Feed + composer (responsive layout) */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background: "rgba(15,23,42,0.96)",
            border: "1px solid rgba(148,163,184,0.6)",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 12,
            minHeight: 180,
          }}
        >
          {/* Left – composer */}
          <div
            style={{
              flex: isMobile ? "0 0 auto" : 1,
              width: isMobile ? "100%" : "auto",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: "0.85rem",
                marginBottom: 2,
              }}
            >
              Log a hero moment
            </div>
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              rows={3}
              placeholder={`Week ${currentWeekNumber} – Finished a key session as ${hero.name}...`}
              style={{
                width: "100%",
                borderRadius: 12,
                padding: 8,
                fontSize: "0.8rem",
                resize: "vertical",
                background: "rgba(15,23,42,0.9)",
                color: "#e5e7eb",
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 4,
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  opacity: 0.8,
                }}
              >
                Tip: log when you crush a workout, hit a PR, or complete a week.
              </div>
              <button
                onClick={handlePostToFeed}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(56,189,248,0.8)",
                  background:
                    "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
                  color: "#e0f2fe",
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                + Add to feed
              </button>
            </div>
          </div>

          {/* Right – compact timeline column (higher contrast, responsive) */}
          <div
            style={{
              width: isMobile ? "100%" : 260,
              maxWidth: isMobile ? "100%" : "40%",
              marginTop: isMobile ? 8 : 0,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              borderLeft: isMobile ? "none" : "1px solid rgba(51,65,85,0.9)",
              borderTop: isMobile ? "1px solid rgba(51,65,85,0.9)" : "none",
              paddingLeft: isMobile ? 0 : 8,
              paddingTop: isMobile ? 8 : 0,
              overflowY: "auto",
              maxHeight: 220,
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                opacity: 0.95,
                color: "#e5e7eb",
              }}
            >
              Timeline
            </div>
            {heroFeed.length === 0 ? (
              <div
                style={{
                  fontSize: "0.8rem",
                  opacity: 0.8,
                  color: "#e5e7eb",
                }}
              >
                Your hero feed is empty. First entry suggestion: “Week 1 –
                locked in my origin as {hero.name}.”
              </div>
            ) : (
              heroFeed.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    borderLeft: "2px solid rgba(56,189,248,0.9)",
                    paddingLeft: 8,
                    paddingTop: 2,
                    paddingBottom: 4,
                    fontSize: "0.8rem",
                    color: "#f9fafb",
                    background: "rgba(15,23,42,0.85)",
                    borderRadius: 6,
                    marginBottom: 3,
                  }}
                >
                  <div
                    style={{
                      opacity: 0.9,
                      marginBottom: 2,
                      color: "#a5b4fc",
                    }}
                  >
                    {entry.date} • Week {entry.week} • {entry.heroName}
                  </div>
                  <div>{entry.text}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Story card preview – RESPONSIVE HORIZONTAL BANNER UNDERNEATH */}
        <div
          style={{
            borderRadius: 16,
            padding: 12,
            background: "rgba(2,6,23,0.98)",
            border: "1px solid rgba(30,64,175,0.7)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: "0.85rem",
              marginBottom: 2,
            }}
          >
            Story Share Card (horizontal preview)
          </div>

          <div
            style={{
              width: "100%",
              borderRadius: 22,
              overflow: "hidden",
              background:
                "radial-gradient(circle at 10% 0%, #38bdf8 0, #020617 40%, #0f172a 100%)",
              boxShadow: "0 14px 36px rgba(15,23,42,0.95)",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              padding: 16,
              gap: 16,
              minHeight: isMobile ? 0 : 150,
            }}
          >
            {/* Left – text stack */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                minWidth: 0,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    opacity: 0.85,
                  }}
                >
                  HERO PHYSIQUE
                </div>
                <div
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {hero.name}{" "}
                  <span
                    style={{
                      fontSize: "0.85rem",
                      opacity: 0.9,
                    }}
                  >
                    • {hero.universe}
                  </span>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    opacity: 0.85,
                    marginBottom: 2,
                  }}
                >
                  {cardSubtitle}
                </div>
                <div
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: "#e0f2fe",
                    marginBottom: 4,
                  }}
                >
                  {cardSummaryLine}
                </div>
                <div
                  style={{
                    fontSize: "0.78rem",
                    opacity: 0.95,
                  }}
                >
                  “{hero.physique}”
                </div>
              </div>
            </div>

            {/* Right – CTA pill */}
            <div
              style={{
                minWidth: isMobile ? "100%" : 150,
                maxWidth: isMobile ? "100%" : 190,
                borderRadius: 999,
                padding: "10px 14px",
                background:
                  "linear-gradient(135deg, rgba(56,189,248,0.7), rgba(15,23,42,0.98))",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 4,
                fontSize: "0.8rem",
              }}
            >
              <span>Started my {blockLength}-week hero arc.</span>
              <span
                style={{
                  fontWeight: 650,
                  whiteSpace: "nowrap",
                  textAlign: isMobile ? "right" : "left",
                }}
              >
                #HeroPhysique
              </span>
            </div>
          </div>

          <div
            style={{
              fontSize: "0.75rem",
              opacity: 0.8,
            }}
          >
            Screenshot this card on your device, add it to your IG / TikTok
            Story, and tag your buddy so they know you’re still in the fight.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengesPanel;
